import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import {
  EmailValidator,
  FormControlComponent,
  InPostPointLocation,
  InPostPointSelect,
  InPostPointSelectComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  PrimengTextareaDirective,
  SelectItem,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { InputText } from 'primeng/inputtext';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Select } from 'primeng/select';
import {
  executeOrNull,
  Guid,
  isNil,
  Nillable,
  valueOrDefault,
} from '@web/utils';
import {
  defer,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  AdminDictionaryCountrySelectItemsProviderService,
  PhoneNumberWithPrefix,
  PhoneNumberWithPrefixComponent,
} from '@web/shared/module/dictionary';
import { AdminOrderShippingMethodSelectItemsProviderService } from '../../providers/admin-order-shipping-method-select-items-provider.service';
import {
  AdminOrderShippingAddressForm,
  AdminOrderShippingAddressFormGroup,
} from '../../models/admin-order-shipping-address-form';
import { AdminOrderShippingMethodCollectionItem } from '../../models/admin-order-shipping-method-collection-item';
import { Textarea } from 'primeng/textarea';
import { AdminShipmentSizeType } from '../../enums/admin-shipment-size-type';
import { AdminShipmentSizeTypeProviderService } from '../../providers/admin-shipment-size-type-provider.service';
import { DatePicker } from 'primeng/datepicker';
import { AdminOrderShipmentItemsControlComponent } from '../admin-order-shipment-items-control/admin-order-shipment-items-control.component';
import { AdminOrderShipmentFormItem } from '../../models/admin-order-shipment-form-item';
import { AdminOrderShipmentComponentGroupType } from '../../enums/admin-order-shipment-component-group-type';

@Component({
  selector: 'app-admin-order-shipping-address-form',
  imports: [
    CommonModule,
    Button,
    FormControlComponent,
    InputText,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    ReactiveFormsModule,
    PrimengSelectDirective,
    Select,
    SpinnerFilledComponent,
    InPostPointSelectComponent,
    PhoneNumberWithPrefixComponent,
    PrimengTextareaDirective,
    Textarea,
    DatePicker,
    AdminOrderShipmentItemsControlComponent,
  ],
  templateUrl: './admin-order-shipping-address-form.component.html',
  styleUrl: './admin-order-shipping-address-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminOrderShippingAddressFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly availableShippingMethodSelectItemsProvider = inject(AdminOrderShippingMethodSelectItemsProviderService);
  /* prettier-ignore */
  private readonly countrySelectItemsProvider = inject(AdminDictionaryCountrySelectItemsProviderService);

  public readonly orderId = input.required<Guid>();
  public readonly shipmentId = input.required<Guid>();
  /* prettier-ignore */
  public readonly data = input<Nillable<AdminOrderShippingAddressForm>>();
  /* prettier-ignore */
  public readonly submitClickEvent = output<AdminOrderShippingAddressForm>();
  public readonly pending = input<boolean>(false);

  protected readonly shippingMethodSelectItems$: Observable<SelectItem<Guid>[]>;
  protected readonly currentCountryCode$: Observable<Nillable<string>>;
  protected readonly shippingMethodHint$: Observable<Nillable<string>>;
  protected readonly stateFormControlAvailability$!: Observable<boolean>;
  protected readonly countrySelectItems$!: Observable<SelectItem<Guid>[]>;
  protected readonly form!: AdminOrderShippingAddressFormGroup;
  protected readonly shipmentItemType = AdminOrderShipmentComponentGroupType;
  /* prettier-ignore */
  protected readonly currentShippingMethod$: Observable<Nillable<AdminOrderShippingMethodCollectionItem>>;
  protected readonly valueOrDefault = valueOrDefault;
  /* prettier-ignore */
  protected readonly shipmentSizeSelectItems$: Observable<SelectItem<AdminShipmentSizeType>[]>;

  /* prettier-ignore */
  private readonly shipmentSizeTypeProvider = inject(AdminShipmentSizeTypeProviderService);
  private readonly defaultSizeType = AdminShipmentSizeType.Small;

  public constructor() {
    this.form = this.buildForm();
    this.countrySelectItems$ = this.prepareCountrySelectItemsSource();
    this.stateFormControlAvailability$ = this.prepareStateAvailabilitySource();
    this.currentCountryCode$ = this.prepareCurrentCountryCodeSource();
    /* prettier-ignore */
    this.shippingMethodHint$ = this.prepareShippingMethodHintSource(this.currentCountryCode$);
    this.shippingMethodSelectItems$ = this.prepareShippingMethodSelectItems();
    this.currentShippingMethod$ = this.prepareCurrentShippingMethodSource();
    this.shipmentSizeSelectItems$ = this.shipmentSizeTypeProvider.selectItems();
  }

  public ngOnInit(): void {
    this.fillForm();
  }

  public handleSubmitButtonClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private toData(): AdminOrderShippingAddressForm {
    const data = this.form.getRawValue();

    return new AdminOrderShippingAddressForm({
      city: data.city,
      companyName: data.companyName,
      countryId: data.countryId,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      postCode: data.postCode,
      state: data.state,
      streetLine1: data.streetLine1,
      streetLine2: data.streetLine2,
      shippingMethodId: data.shippingMethodId,
      shippingPoint: data.shippingPoint,
      scheduledShippingDate: data.scheduledShippingDate,
      size: data.size,
      comment: data.comment,
      boxes: valueOrDefault(data.boxes, []),
      products: valueOrDefault(data.products, []),
      cosmetics: valueOrDefault(data.cosmetics, []),
      boxesReadonlyReason: this.data()?.boxesReadonlyReason,
      productsReadonlyReason: this.data()?.productsReadonlyReason,
      cosmeticsReadonlyReason: this.data()?.cosmeticsReadonlyReason,
    });
  }

  private buildForm(): AdminOrderShippingAddressFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      city: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminOrderShippingAddressForm.CityMaxLength)]),
      companyName: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminOrderShippingAddressForm.CompanyNameMaxLength)]),
      countryId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      email: this.builder.control<Nillable<string>>(null, [Validators.required, EmailValidator.email]),
      firstName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminOrderShippingAddressForm.FirstNameMaxLength)]),
      lastName: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminOrderShippingAddressForm.LastNameMaxLength)]),
      phone: this.builder.control<Nillable<PhoneNumberWithPrefix>>(null, [Validators.required]),
      postCode: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminOrderShippingAddressForm.PostCodeMaxLength)]),
      state: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminOrderShippingAddressForm.StateMaxLength)]),
      streetLine1: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(AdminOrderShippingAddressForm.StreetMaxLength)]),
      streetLine2: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminOrderShippingAddressForm.StreetMaxLength)]),
      shippingMethodId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      shippingPoint: this.builder.control<Nillable<InPostPointSelect>>(null, [Validators.required]),
      scheduledShippingDate: this.builder.control<Nillable<Date>>(null, [Validators.required]),
      size: this.builder.control<Nillable<AdminShipmentSizeType>>(this.defaultSizeType, [Validators.required]),
      comment: this.builder.control<Nillable<string>>(null, [Validators.maxLength(AdminOrderShippingAddressForm.CommentMaxLength)]),
      boxes: this.builder.nonNullable.control<AdminOrderShipmentFormItem[]>([]),
      products: this.builder.nonNullable.control<AdminOrderShipmentFormItem[]>([]),
      cosmetics: this.builder.nonNullable.control<AdminOrderShipmentFormItem[]>([]),
    });
  }

  private fillForm(): void {
    const data = this.data();

    this.form.patchValue({
      city: data?.city,
      companyName: data?.companyName,
      countryId: data?.countryId,
      email: data?.email,
      firstName: data?.firstName,
      lastName: data?.lastName,
      phone: data?.phone,
      postCode: data?.postCode,
      state: data?.state,
      streetLine1: data?.streetLine1,
      streetLine2: data?.streetLine2,
      shippingMethodId: data?.shippingMethodId,
      shippingPoint: data?.shippingPoint,
      scheduledShippingDate: data?.scheduledShippingDate,
      size: data?.size,
      comment: data?.comment,
      boxes: data?.boxes,
      products: data?.products,
      cosmetics: data?.cosmetics,
    });
  }

  private prepareCountrySelectItemsSource(): Observable<SelectItem<Guid>[]> {
    return this.countrySelectItemsProvider.fetchSelectItems('id');
  }

  private prepareStateAvailabilitySource(): Observable<boolean> {
    const { countryId, state } = this.form.controls;

    return countryId.valueChanges.pipe(
      startWith(countryId.value),
      switchMap((countryId) =>
        isNil(countryId)
          ? of(null)
          : this.countrySelectItemsProvider.fetchIsoCodeForId(countryId),
      ),
      map((code) => code !== 'PL' && !isNil(code)),
      tap((isAvailable) => (isAvailable ? state.enable() : state.disable())),
    );
  }

  private prepareCurrentCountryCodeSource(): Observable<Nillable<string>> {
    return defer(() =>
      this.form.controls.countryId.valueChanges.pipe(
        startWith(this.form.controls.countryId.value),
        distinctUntilChanged(),
        switchMap((countryId) => {
          return isNil(countryId)
            ? of(null)
            : this.countrySelectItemsProvider.fetchIsoCodeForId(countryId);
        }),
      ),
    );
  }

  /* prettier-ignore */
  private prepareShippingMethodHintSource(currentCountryCode$: Observable<Nillable<string>>): Observable<Nillable<string>> {
    return defer(()=> currentCountryCode$.pipe(
      map((code) => !isNil(code)),
      tap((availability) => {
        const { shippingMethodId } = this.form.controls;

        availability
          ? shippingMethodId.enable({ emitEvent: false })
          : shippingMethodId.disable({ emitEvent: false });
      }),
      map((availability) => availability ? null : 'Aby wybrać rodzaj wysyłki, należy najpierw wskazać kraj.'),
    ));
  }

  private prepareShippingMethodSelectItems(): Observable<SelectItem<Guid>[]> {
    return defer(() =>
      this.form.controls.countryId.valueChanges.pipe(
        tap(() => {
          this.form.controls.shippingMethodId.setValue(null);
        }),
        startWith(this.form.controls.countryId.value),
        distinctUntilChanged(),
        switchMap((countryId) =>
          isNil(countryId)
            ? of([])
            : this.availableShippingMethodSelectItemsProvider.fetchSelectItems({
                orderId: this.orderId(),
                shipmentId: this.shipmentId(),
                key: 'id',
                countryId,
              }),
        ),
      ),
    );
  }

  private prepareCurrentShippingMethodSource(): Observable<
    Nillable<AdminOrderShippingMethodCollectionItem>
  > {
    return defer(() =>
      this.form.controls.shippingMethodId.valueChanges.pipe(
        startWith(this.form.controls.shippingMethodId.value),
        switchMap((id) =>
          isNil(id)
            ? of(null)
            : this.availableShippingMethodSelectItemsProvider.fetchShippingMethodById(
                {
                  id,
                  countryId: this.form.controls.countryId.value,
                  orderId: this.orderId(),
                  shipmentId: this.shipmentId(),
                },
              ),
        ),
        tap((method) => {
          const { shippingPoint } = this.form.controls;

          if (isNil(method)) {
            shippingPoint.disable({ emitEvent: false });

            return;
          }

          if (
            [
              method.isDomesticInPostGeowidget,
              method.isInternationalInPostGeowidget,
            ].some((result) => result)
          ) {
            shippingPoint.enable({ emitEvent: false });

            shippingPoint.setValue(
              executeOrNull(
                method.inPostGeowidgetProperty,
                (property) =>
                  new InPostPointSelect({
                    name: property.value,
                    description: property.description,
                    location: new InPostPointLocation({
                      longitude: property.longitude!,
                      latitude: property.latitude!,
                    }),
                  }),
              ),
            );

            return;
          }

          shippingPoint.disable({ emitEvent: false });
        }),
      ),
    );
  }
}
